import JobService from '@/services/job-service'
import { buildInvoiceDownloadFilename, sanitizeInvoiceFilenameBase } from '@/utils/invoice-filename'

/**
 * Resolve a stored invoice/revision file path to a data: URL for iframe preview.
 * Returns null when path is missing or fetch fails.
 */
export async function loadInvoiceFileDataUrl(filePath) {
  if (!filePath || typeof filePath !== 'string') return null
  if (/^data:/i.test(filePath)) return filePath
  if (/^blob:/i.test(filePath)) return filePath
  if (/^https?:\/\//i.test(filePath)) return filePath

  try {
    const key = filePath.replace('/', '%2F')
    const res = await JobService.getFileBase64(key)
    const base64 = res && res.file
    if (!base64) return null
    return `data:application/pdf;base64,${base64}`
  } catch (_) {
    return null
  }
}

function documentUrlsObject(rev) {
  let urls = rev && (rev.document_urls || rev.documentUrls)
  if (typeof urls === 'string') {
    try {
      urls = JSON.parse(urls)
    } catch (_) {
      return null
    }
  }
  if (!urls || typeof urls !== 'object' || Array.isArray(urls)) return null
  return urls
}

function pathFromDocumentUrls(urls, ...keys) {
  if (!urls) return null
  for (const k of keys) {
    const v = urls[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return null
}

/** Collect downloadable paths from a revision history row. */
export function revisionDocumentLinks(rev) {
  if (!rev || typeof rev !== 'object') return []
  const links = []
  const revNum = rev.revision_number != null && rev.revision_number !== ''
    ? rev.revision_number
    : rev.id
  const prefix = revNum != null && revNum !== '' ? `Rev #${revNum}` : 'Revision'
  const files = rev.files || {}
  const docUrls = documentUrlsObject(rev)
  const seen = new Set()

  const add = (kind, label, path) => {
    if (!path || seen.has(kind)) return
    seen.add(kind)
    links.push({ label, path, kind })
  }

  add(
    'revised',
    `${prefix} — Revised invoice`,
    files.revised_invoice_url
      || rev.revised_invoice_file_path
      || pathFromDocumentUrls(docUrls, 'revised_invoice_url', 'revised_invoice', 'revised', 'current', 'current_pdf'),
  )
  add(
    'r1',
    `${prefix} — R1 document`,
    files.r1_upload_url
      || rev.r1_upload_file_path
      || pathFromDocumentUrls(docUrls, 'r1_upload_url', 'r1', 'r1_file'),
  )
  add(
    'note',
    `${prefix} — Debit/Credit note`,
    files.note_file_url
      || rev.note_file_path
      || pathFromDocumentUrls(docUrls, 'note_file_url', 'note', 'debit_note', 'credit_note'),
  )
  add(
    'original',
    `${prefix} — Original snapshot`,
    files.original_invoice_url
      || rev.original_invoice_file_path
      || files.previous_pdf_url
      || rev.previous_pdf
      || rev.old_file
      || rev.old_pdf
      || rev.previous_file_path
      || pathFromDocumentUrls(docUrls, 'original_invoice_url', 'previous_pdf', 'old_file', 'original', 'previous'),
  )

  return links
}

export function formatRevisionSnapshotLabel(rev) {
  if (!rev) return ''
  const parts = []
  const num = rev.revision_number != null && rev.revision_number !== ''
    ? rev.revision_number
    : rev.id
  if (num != null && num !== '') parts.push(`Revision #${num}`)
  const raw = rev.created_at || rev.createdAt
  if (raw) {
    const formatted = String(raw).replace(/T/, ' ').replace(/\..+/, '')
    parts.push(formatted)
  }
  const changeType = rev.amount_change_type || rev.change_type
  if (rev.revision_type === 'basic') parts.push('Basic info')
  else if (rev.revision_type === 'amount') {
    parts.push(
      changeType === 'decrease' || changeType === 'credit'
        ? 'Credit note'
        : (changeType === 'increase' || changeType === 'debit' ? 'Debit note' : 'Amount'),
    )
  } else if (changeType) {
    parts.push(String(changeType))
  }
  if (rev.note_type) parts.push(String(rev.note_type))
  const actor = rev.initiated_by || rev.created_by
  if (actor) parts.push(`by ${actor}`)
  return parts.join(' · ') || 'Revision'
}

function firstNonEmptyPath(...candidates) {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

/**
 * Probe invoice detail + revision history for current (new) vs previous (old) PDF paths.
 * Keeps current filename convention unchanged for the live/current file.
 */
export function resolveInvoiceVersionFiles(invoice = null, revisionHistory = []) {
  const inv = invoice && typeof invoice === 'object' ? invoice : {}
  const files = inv.files && typeof inv.files === 'object' ? inv.files : {}
  const revisions = Array.isArray(revisionHistory) ? [...revisionHistory] : []
  revisions.sort((a, b) => {
    const na = Number(a.revision_number)
    const nb = Number(b.revision_number)
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb
    const ta = Date.parse(a.created_at || a.createdAt || '') || 0
    const tb = Date.parse(b.created_at || b.createdAt || '') || 0
    if (ta !== tb) return ta - tb
    return (Number(a.id) || 0) - (Number(b.id) || 0)
  })
  const latest = revisions.length ? revisions[revisions.length - 1] : null
  const first = revisions.length ? revisions[0] : null
  const latestFiles = (latest && latest.files) || {}
  const firstFiles = (first && first.files) || {}
  const latestDocUrls = documentUrlsObject(latest)
  const firstDocUrls = documentUrlsObject(first)
  const prior = revisions.length > 1 ? revisions[revisions.length - 2] : null
  const priorFiles = (prior && prior.files) || {}
  const priorDocUrls = documentUrlsObject(prior)

  const currentPath = firstNonEmptyPath(
    files.current_pdf,
    files.current_file,
    files.revised_invoice_url,
    inv.current_pdf,
    inv.current_file,
    inv.revised_invoice_file_path,
    inv.revised_invoice_url,
    latest && (latest.revised_invoice_file_path || latestFiles.revised_invoice_url),
    pathFromDocumentUrls(latestDocUrls, 'revised_invoice_url', 'revised_invoice', 'revised', 'current'),
    inv.generated_pdf_path,
    inv.generated_pdf,
    inv.file,
    inv.file_path,
    inv.invoice_file,
    inv.invoice_file_path,
  )

  const oldPath = firstNonEmptyPath(
    files.previous_pdf,
    files.old_file,
    files.old_pdf,
    files.original_invoice_url,
    inv.previous_pdf,
    inv.previous_file,
    inv.previous_file_path,
    inv.previous_invoice_file_path,
    inv.old_file,
    inv.old_pdf,
    inv.old_file_path,
    inv.original_invoice_file_path,
    inv.original_file,
    inv.original_file_path,
    first && (
      first.original_invoice_file_path
      || firstFiles.original_invoice_url
      || first.previous_pdf
      || first.old_file
      || first.previous_file_path
      || pathFromDocumentUrls(firstDocUrls, 'original_invoice_url', 'previous_pdf', 'old_file', 'original')
    ),
    // Prior revision's revised file becomes "old" when a newer revision exists
    prior
      ? (
        prior.revised_invoice_file_path
        || priorFiles.revised_invoice_url
        || pathFromDocumentUrls(priorDocUrls, 'revised_invoice_url', 'revised_invoice', 'revised', 'current')
      )
      : null,
  )

  // Avoid treating the same path as both current and old
  const distinctOld = oldPath && currentPath && oldPath === currentPath ? null : oldPath

  return {
    currentPath,
    oldPath: distinctOld,
    hasCurrent: !!currentPath,
    hasOld: !!distinctOld,
  }
}

/**
 * Current download keeps today's filename pattern.
 * Old download adds a clear "(Previous)" suffix without renaming the live file.
 */
export function buildVersionDownloadFilename(invoice = {}, version = 'current') {
  const base = buildInvoiceDownloadFilename(invoice || {}, 'pdf')
  if (version !== 'old' && version !== 'previous') return base
  const withoutExt = base.replace(/\.pdf$/i, '')
  return `${sanitizeInvoiceFilenameBase(`${withoutExt} (Previous)`)}.pdf`
}

const SUPPORTING_DOC_SOURCE_KEYS = [
  'supporting_documents',
  'supportingDocuments',
  'supporting_docs',
  'supporting_document',
  'supportingDocument',
]

/** Extensions that belong on the supporting-docs row, not the generated invoice PDF. */
const SUPPORTING_ONLY_EXTENSIONS = ['png', 'jpg', 'jpeg', 'txt', 'xls', 'xlsx', 'csv', 'gif', 'webp']

function tryParseJsonMaybe(value) {
  if (typeof value !== 'string') return value
  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      return JSON.parse(trimmed)
    } catch (_) {
      return value
    }
  }
  return value
}

function filenameFromStoredPath(path) {
  if (!path || typeof path !== 'string') return ''
  const clean = path.split('?')[0].split('#')[0]
  const segment = clean.split('/').filter(Boolean).pop() || clean
  try {
    return decodeURIComponent(segment)
  } catch (_) {
    return segment
  }
}

function fileExtension(nameOrPath) {
  const base = filenameFromStoredPath(nameOrPath || '')
  const match = base.toLowerCase().match(/\.([a-z0-9]+)$/)
  return match ? match[1] : ''
}

function mimeFromFilename(nameOrPath) {
  const map = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    txt: 'text/plain',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
  }
  return map[fileExtension(nameOrPath)] || 'application/octet-stream'
}

function isHttpishUrl(value) {
  return typeof value === 'string' && /^(https?:\/\/|blob:|data:)/i.test(value.trim())
}

function normalizeSupportingDoc(entry, fallbackIndex) {
  if (entry == null || entry === '') return null
  if (typeof File !== 'undefined' && entry instanceof File) {
    return {
      name: entry.name || `Supporting document ${fallbackIndex + 1}`,
      url: null,
      path: null,
      file: entry,
    }
  }
  if (typeof entry === 'string') {
    const parsed = tryParseJsonMaybe(entry)
    if (parsed !== entry && parsed != null) return normalizeSupportingDoc(parsed, fallbackIndex)
    const trimmed = entry.trim()
    if (!trimmed) return null
    return {
      name: filenameFromStoredPath(trimmed) || `Supporting document ${fallbackIndex + 1}`,
      url: isHttpishUrl(trimmed) ? trimmed : null,
      path: isHttpishUrl(trimmed) ? null : trimmed,
      file: null,
    }
  }
  if (typeof entry !== 'object') return null
  const url = firstNonEmptyPath(
    entry.url,
    entry.file_url,
    entry.download_url,
    entry.invoice_file_url,
    entry.href,
  )
  const path = firstNonEmptyPath(
    entry.path,
    entry.file_path,
    entry.file,
    entry.invoice_file,
    entry.invoice_file_path,
    entry.invoiceFile,
    entry.key,
  )
  const explicitName = firstNonEmptyPath(
    entry.name,
    entry.file_name,
    entry.filename,
    entry.original_name,
    entry.document_name,
  )
  if (!url && !path && !explicitName) return null
  const name = explicitName
    || filenameFromStoredPath(url || path)
    || `Supporting document ${fallbackIndex + 1}`
  return { name, url: url || null, path: path || null, file: null }
}

function collectSupportingEntries(value, bucket) {
  const parsed = tryParseJsonMaybe(value)
  if (parsed == null || parsed === '') return
  if (Array.isArray(parsed)) {
    parsed.forEach((item) => {
      const doc = normalizeSupportingDoc(item, bucket.length)
      if (doc) bucket.push(doc)
    })
    return
  }
  const doc = normalizeSupportingDoc(parsed, bucket.length)
  if (doc) bucket.push(doc)
}

function supportingDocIdentity(doc) {
  return String(doc.url || doc.path || doc.name || '').trim().toLowerCase()
}

/**
 * Collect supporting attachments from a GET invoice / list payload.
 * Reads supporting_document(s), invoiceFile, and file URLs — not signed/revision PDFs.
 */
export function extractSupportingDocuments(invoice) {
  if (!invoice || typeof invoice !== 'object') return []
  const bucket = []
  const files = invoice.files && typeof invoice.files === 'object' && !Array.isArray(invoice.files)
    ? invoice.files
    : {}
  const docUrls = documentUrlsObject(invoice)

  SUPPORTING_DOC_SOURCE_KEYS.forEach((key) => {
    collectSupportingEntries(invoice[key], bucket)
    collectSupportingEntries(files[key], bucket)
    if (docUrls) collectSupportingEntries(docUrls[key], bucket)
  })

  collectSupportingEntries(invoice.invoiceFile, bucket)
  collectSupportingEntries(invoice.invoice_file, bucket)
  collectSupportingEntries(invoice.invoice_file_url, bucket)
  collectSupportingEntries(invoice.invoice_file_path, bucket)

  // Create wizard currently persists the first supporting file as invoiceFile → `file`.
  // Show that path only when it is clearly a supporting type (not the generated PDF).
  const primaryStored = firstNonEmptyPath(invoice.file, invoice.file_path)
  if (primaryStored && SUPPORTING_ONLY_EXTENSIONS.includes(fileExtension(primaryStored))) {
    collectSupportingEntries(primaryStored, bucket)
  }

  const seen = new Set()
  return bucket.filter((doc) => {
    const id = supportingDocIdentity(doc)
    if (!id || seen.has(id)) return false
    seen.add(id)
    return true
  }).map((doc, idx) => ({
    ...doc,
    key: supportingDocIdentity(doc) || String(idx),
  }))
}

function triggerBrowserDownload(href, filename) {
  const link = document.createElement('a')
  link.href = href
  link.download = filename || 'document'
  link.target = '_blank'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

async function loadStoredFileDataUrl(filePath, mime = 'application/pdf') {
  if (!filePath || typeof filePath !== 'string') return null
  if (isHttpishUrl(filePath)) return filePath.trim()
  try {
    const key = filePath.replace(/^\//, '').replace('/', '%2F')
    const res = await JobService.getFileBase64(key)
    const base64 = res && res.file
    if (!base64) return null
    if (typeof base64 === 'string' && /^https?:\/\//i.test(base64)) return base64
    return `data:${mime};base64,${base64}`
  } catch (_) {
    return null
  }
}

export function supportingDocCanOpen(doc) {
  if (!doc) return false
  if (typeof File !== 'undefined' && doc.file instanceof File) return true
  return !!(doc.url || doc.path)
}

export function attachmentPreviewKind(nameOrPath) {
  const ext = fileExtension(nameOrPath)
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image'
  if (ext === 'pdf') return 'pdf'
  if (ext === 'txt') return 'text'
  return 'other'
}

function blobUrlFromBase64(base64, mime) {
  const byteChars = atob(base64)
  const bytes = new Uint8Array(byteChars.length)
  for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i)
  return URL.createObjectURL(new Blob([bytes], { type: mime || 'application/octet-stream' }))
}

/**
 * Resolve a supporting document to an embeddable preview URL.
 * Uses blob: URLs for stored files so the viewer is not blocked the way
 * window.open(data:…) often is after an async fetch.
 */
export async function resolveInvoiceAttachmentPreview(doc) {
  if (!doc) return null
  const href = doc.url || doc.path || ''
  const name = doc.name || filenameFromStoredPath(href) || 'document'
  const kind = attachmentPreviewKind(name || href)
  const mime = mimeFromFilename(name || href)

  if (typeof File !== 'undefined' && doc.file instanceof File) {
    return { src: URL.createObjectURL(doc.file), kind, name, mime, revoke: true }
  }
  if (!href) return null

  if (isHttpishUrl(href)) {
    const trimmed = href.trim()
    if (/^data:/i.test(trimmed) && !/^data:text\//i.test(trimmed)) {
      try {
        const res = await fetch(trimmed)
        const blob = await res.blob()
        return { src: URL.createObjectURL(blob), kind, name, mime, revoke: true }
      } catch (_) {
        return { src: trimmed, kind, name, mime, revoke: false }
      }
    }
    return { src: trimmed, kind, name, mime, revoke: false }
  }

  try {
    const key = String(href).replace(/^\//, '').replace('/', '%2F')
    const res = await JobService.getFileBase64(key)
    if (!(res && res.file)) return null
    if (typeof res.file === 'string' && /^https?:\/\//i.test(res.file)) {
      return { src: res.file, kind, name, mime, revoke: false }
    }
    return { src: blobUrlFromBase64(res.file, mime), kind, name, mime, revoke: true }
  } catch (_) {
    return null
  }
}

export async function openInvoiceAttachment(doc) {
  const preview = await resolveInvoiceAttachmentPreview(doc)
  if (!preview || !preview.src) return false
  const previewable = preview.kind === 'pdf' || preview.kind === 'image' || preview.kind === 'text'
  if (!previewable) {
    if (preview.revoke && preview.src) URL.revokeObjectURL(preview.src)
    return downloadInvoiceAttachment(doc)
  }
  window.open(preview.src, '_blank', 'noopener,noreferrer')
  if (preview.revoke) {
    setTimeout(() => {
      try { URL.revokeObjectURL(preview.src) } catch (_) { /* already revoked */ }
    }, 60000)
  }
  return true
}

export async function downloadInvoiceAttachment(doc) {
  if (!doc) return false
  const name = doc.name || 'document'
  if (typeof File !== 'undefined' && doc.file instanceof File) {
    const objectUrl = URL.createObjectURL(doc.file)
    triggerBrowserDownload(objectUrl, name)
    URL.revokeObjectURL(objectUrl)
    return true
  }
  const href = doc.url || doc.path
  if (!href) return false
  if (isHttpishUrl(href)) {
    triggerBrowserDownload(href.trim(), name)
    return true
  }
  try {
    const key = String(href).replace(/^\//, '').replace('/', '%2F')
    const res = await JobService.getFileBase64(key)
    if (!(res && res.file)) return false
    if (typeof res.file === 'string' && /^https?:\/\//i.test(res.file)) {
      triggerBrowserDownload(res.file, name)
      return true
    }
    const byteChars = atob(res.file)
    const bytes = new Uint8Array(byteChars.length)
    for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i)
    const blob = new Blob([bytes], { type: mimeFromFilename(name || href) })
    const url = URL.createObjectURL(blob)
    triggerBrowserDownload(url, name)
    URL.revokeObjectURL(url)
    return true
  } catch (_) {
    return false
  }
}

/**
 * Trigger a browser download for a stored path / URL / data URL.
 */
function bytesFromBase64(base64) {
  const byteChars = atob(base64)
  const bytes = new Uint8Array(byteChars.length)
  for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i)
  return bytes
}

/**
 * Fetch a supporting-doc entry as a browser File for Duplicate → Create.
 * Returns null when the API has no path/bytes (caller must require a new upload).
 */
export async function loadSupportingDocAsFile(doc) {
  if (!doc) return null
  if (typeof File !== 'undefined' && doc.file instanceof File) return doc.file
  const href = doc.url || doc.path
  if (!href) return null
  const name = doc.name || filenameFromStoredPath(href) || 'supporting-document'
  const mime = mimeFromFilename(name || href)

  if (isHttpishUrl(href)) {
    const trimmed = href.trim()
    if (/^data:/i.test(trimmed)) {
      try {
        const res = await fetch(trimmed)
        const blob = await res.blob()
        if (typeof File === 'undefined') return blob
        return new File([blob], name, { type: blob.type || mime })
      } catch (_) {
        return null
      }
    }
    try {
      const res = await fetch(trimmed)
      if (!res.ok) return null
      const blob = await res.blob()
      if (typeof File === 'undefined') return blob
      return new File([blob], name, { type: blob.type || mime })
    } catch (_) {
      return null
    }
  }

  try {
    const key = String(href).replace(/^\//, '').replace('/', '%2F')
    const res = await JobService.getFileBase64(key)
    if (!(res && res.file)) return null
    if (typeof res.file === 'string' && /^https?:\/\//i.test(res.file)) {
      const remote = await fetch(res.file)
      if (!remote.ok) return null
      const blob = await remote.blob()
      if (typeof File === 'undefined') return blob
      return new File([blob], name, { type: blob.type || mime })
    }
    const bytes = bytesFromBase64(res.file)
    if (typeof File === 'undefined') return new Blob([bytes], { type: mime })
    return new File([bytes], name, { type: mime })
  } catch (_) {
    return null
  }
}

export async function downloadInvoiceFile(filePath, filename = 'Invoice.pdf') {
  if (!filePath) return false
  const name = filename || 'Invoice.pdf'

  if (/^https?:\/\//i.test(filePath) || /^blob:/i.test(filePath) || /^data:/i.test(filePath)) {
    const link = document.createElement('a')
    link.href = filePath
    link.download = name
    link.target = '_blank'
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return true
  }

  try {
    const key = filePath.replace(/^\//, '').replace('/', '%2F')
    const res = await JobService.getFileBase64(key)
    if (!(res && res.file)) return false
    const byteChars = atob(res.file)
    const bytes = new Uint8Array(byteChars.length)
    for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i)
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    return true
  } catch (_) {
    return false
  }
}
